import classNames from 'classnames';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { RepoItem } from '../autogenerated/ZoomBridge';
import { OsUtil } from '../basic/OsUtil';
import System from '../basic/System';
import { CompareView } from '../compare/CompareRevisions';
import { FolderDetails } from '../components/folder-details/FolderDetails';
import CheckoutController from '../controllers/CheckoutController';
import t from '../i18n/I18n';
import LazyLoad from '../lazyload/LazyLoad';
import ArchiveDetailsModal from '../modals/archive/ArchiveDetailsModal';
import ArchiveModal from '../modals/archive/ArchiveModal';
import RestoreModal from '../modals/restore/RestoreModal';
import Popover from '../Popover';
import DelinkQueryExecutor from '../queries/DelinkQueryExecutor';
import { favAddRemoveOperation, removeFavItem } from '../queries/fav/FavoriteUtil';
import LockQuery from '../queries/lock/LockQuery';
import UnLockQuery from '../queries/lock/UnLockQuery';
import PurgeQuery from '../queries/purge/PurgeQuery';
import ResurrectQuery from '../queries/resurrect/ResurrectQuery';
import S3Upload from '../s3-operation/S3Upload';
import SmartCopyLinkDialog from '../smart-copy-link/SmartCopyLinkDialog';
import { showSuccessMsg } from '../store/StoreUtil';
import { BrowseExtUtil } from '../tabs/browse-external/BrowseExtUtil';
import InfoModal from '../tabs/browse-external/InfoModal';
import { ThemeSetup } from '../themes/ThemeSetup';
import { ClipBoardUtil } from '../util/ClipBoardUtil';
import { archiveStatusEnum, CONFLICT, MODIFIED, UPTODATE_NEEDED_ICON, VERSIONED } from '../util/Constants';
import FileInfoUtil from '../util/FileInfoUtil';
import NativeActionUtil from '../util/NativeActionUtil';
import ParamsUtil from '../util/ParamsUtil';
import { getAssetPathFuids } from '../util/RepoItemUtil';
import { isSmallScreen } from '../util/ScreenUtil';
import { filterAssets, getAssetProps } from '../util/SelectedItemUtil';
import ViewUtil from '../ViewUtil';
import Portal from './../common/Portal';
import Action from './Action';
import { AssetStatusView } from './AssetPropertyView';
import Button from './Button';
import Separator from './form/Separator';
import './MorePopover.css';
import { ProgressDialog } from './progress-dialog/ProgressDialog';

const RenameModal = LazyLoad(() => import('../modals/rename/RenameModal'));
const AddSpotlightModal = LazyLoad(() => import('../modals/spotlight/AddSpotlightModal'));
const DeleteAssetsModal = LazyLoad(() => import('../modals/delete/DeleteAssetsModal'));
const RemoveAssetsModal = LazyLoad(() => import('../modals/remove/RemoveAssetsModal'));
const AddToCollectionModal = LazyLoad(() => import('../modals/add/AddToCollectionModal'));
const EditAssetsModal = LazyLoad(() => import('../modals/edit/EditAssetsModal'));

const ADD_TO_COLLECTION = 'ADD_TO_COLLECTION';
const EDIT_METADATA_MODAL = 'EDIT_METADATA_MODAL';
const PROGRESS_MODAL = 'PROGRESS_MODAL';
const RESTORE_MODAL = 'RESTORE_MODAL';
const ARCHIVE_MODAL = 'ARCHIVE_MODAL';
const ARCHIVE_DETAILS_MODAL = 'ARCHIVE_DETAILS_MODAL';
const SMART_COPY_MODAL = 'SMART_COPY_MODAL';
const SMART_LINK_MODAL = 'SMART_LINK_MODAL';
const COMPARE_VIEW_MODAL = 'COMPARE_VIEW_MODAL';
const FOLDER_DETAILS = 'FOLDER_DETAILS';
const S3_UPLOAD_MODAL = 'S3_UPLOAD_MODAL';
const EXT_FILE_INFO_MODAL = 'EXT_FILE_INFO_MODAL';

export default class MorePopover extends React.PureComponent {
  portalElement: HTMLDivElement = null;

  static propTypes = {
    top: PropTypes.any,
    bottom: PropTypes.any,
    left: PropTypes.any,
    right: PropTypes.any,
    height: PropTypes.any,
    width: PropTypes.any,
    backgroundImage: PropTypes.any,
    asset: PropTypes.object,
    iconStyle: PropTypes.object,
    onDeleteSuccess: PropTypes.func,
    showAssetDetail: PropTypes.func,
    permission: PropTypes.object,
    onMenuClose: PropTypes.func,
    onMenuOpen: PropTypes.func,
    fetchAssets: PropTypes.func,
    onRenameSuccess: PropTypes.func,
    showPopover: PropTypes.any,
    onRefreshPWD: PropTypes.func,
    extDetails: PropTypes.object,
    onRefresh: PropTypes.func
  };

  static defaultProps = {
    onMenuClose: noop,
    onMenuOpen: noop,
    onPopoverOpen: noop,
    onPopoverClose: noop,
    onRemoveFromCollectionClick: noop,
    backgroundImage: 'url(images/more.svg)',
    showPopover: false,
    closeContextMenu: noop,
    extDetails: {}
  };

  //containsFolder = undefined;
  //containFiles = undefined;
  containWCFile = undefined;
  isMac: boolean = false;
  contextMenuCall: boolean = undefined;
  state = {
    showPopover: false,
    modal: null
  };

  componentWillMount() {
    this.isMac = OsUtil.isMAC();
    this.selectedAssets = undefined;
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.showPopover && !prevState.showPopover) this.props.onMenuOpen();
    else if (!this.state.showPopover && prevState.showPopover) this.props.onMenuClose();

    if (prevState.modal == null && this.state.modal != null) this.props.onPopoverOpen();
    else if (prevState.modal != null && this.state.modal == null) this.props.onPopoverClose();
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.props.onMenuClose();
    this.props.onPopoverClose();
  }

  setStateOnMountCheck(state) {
    if (this._isMounted) {
      this.setState(state);
    }
  }

  onLockClick = (): void => {
    const assets = this.getAssets();
    let { remotePaths } = filterAssets(assets, -1, true);
    new LockQuery(this.getProgressOnQuery).run(remotePaths, assets);
    this.onClose();
  };

  onUnLockClick = (): void => {
    const assets = this.getAssets();
    let { assetIds } = filterAssets(assets, -1, false, true);
    new UnLockQuery(this.getProgressOnQuery).run(assetIds, assets[0].getRemotePath(), assets);
    this.onClose();
  };

  browserToParent = () => {
    const asset = this.getAssets()[0];
    let parentDirPath;

    getAssetPathFuids(asset.getFuid()).then((path) => (parentDirPath = path));

    return () => {
      this.props.onParentFolderClick(parentDirPath);
      this.onClose();
    };
  };

  onConflictResolveClick = (): void => {
    let { localPaths } = filterAssets(this.getAssets(), -1, false, false, true);
    NativeActionUtil.runExecQuery(localPaths, 'resolve');
    this.onClose();
  };

  onUpdateClick = (): void => {
    let { localPaths } = filterAssets(this.getAssets(), -1, false, false, true);
    NativeActionUtil.runExecQuery(localPaths, 'update');
    this.onClose();
  };

  onCheckInClick = (): void => {
    let { localPaths } = filterAssets(this.getAssets(), -1, false, false, true);
    NativeActionUtil.runExecQuery(localPaths, 'commit');
    this.onClose();
  };

  onOpenClick = (): void => {
    const asset = this.getAssets()[0];
    const extInfo = asset.extraInfo;
    if (extInfo && extInfo.isIsConnected()) {
      NativeActionUtil.runExecQuery([extInfo.getDragPath()], 'open');
    } else {
      const checkoutController = new CheckoutController(this.getAssets(), false, false, null, 'open');
      checkoutController.run();
    }
    this.onClose();
  };

  onSmartCopyLink = (modal) => {
    this.setState({ modal });
    this.onClose();
  };

  onEditClick = (): void => {
    let { localPaths } = filterAssets(this.getAssets(), -1, false, false, true);
    NativeActionUtil.runExecQuery(localPaths, 'open');
    this.onClose();
  };

  runRevealExecQuery() {
    let { localPaths } = filterAssets(this.getAssets(), -1, false, false, true);
    NativeActionUtil.runExecQuery(localPaths, 'reveal');
    this.onClose();
  }

  showOnLegacyBrowser = (): void => {
    const assets: Array<RepoItem> = this.getAssets();
    NativeActionUtil.runExecQuery(null, 'asset_browser', undefined, assets[0].getFuid());
    this.onClose();
  };

  copyAssetId = (): void => {
    const assets: Array<RepoItem> = this.getAssets();
    const items = [];
    assets.forEach((repo) => {
      items.push(repo.getFuid());
    });
    const text = items.join(', ');
    ClipBoardUtil.copyToClipBoard(text);
    this.onClose();
  };

  copyAssetURL = (): void => {
    const asset = this.getAssets()[0];
    const __url = ParamsUtil.generateWebclientURL(asset);
    ClipBoardUtil.copyToClipBoard(__url);
    this.onClose();
  };

  copyAssetPath = (): void => {
    const assets: Array<RepoItem> = this.getAssets();
    const items = [];
    assets.forEach((repo) => {
      items.push(repo.getRemotePath());
    });
    const text = items.join(', ');
    ClipBoardUtil.copyToClipBoard(text);
    this.onClose();
  };

  addToCollection = (): void => {
    this.setState({ modal: ADD_TO_COLLECTION });
    this.onClose();
  };

  onDownloadClick = () => {
    this.onClose();
    this.setState({ showPopover: false }, () => {
      const assets = this.getAssets();
      window.gs.selectedAssets = assets;
      window.gs.isDownloadClick = true;
      ViewUtil.switchToAssetDetailView(window.gs.selectedAssets[0]._fuid);
    });

    // if (assets.length > 1) {
    //   ViewUtil.switchToAssetDetailView(assets[0].getFuid());
    // } else {
    //   this.onClose();
    //   this.props.downloadAsset(assets[0]);
    // }
  };

  editMetadata = () => {
    this.setState({ modal: EDIT_METADATA_MODAL });
    this.onClose();
  };

  getAssets = () => {
    if (!this.selectedAssetsCache) {
      const assets: Array<RepoItem> = [];
      if (this.contextMenuCall) {
        const selected: Array<RepoItem> = window.gs.selectedAssets;
        if (selected.length) assets.push(...selected);
        else assets.push(this.props.asset);
      } else {
        assets.push(this.props.asset);
      }
      this.selectedAssetsCache = assets;
    }
    return this.selectedAssetsCache;
  };

  onClickDelete = () => {
    this.setState({ modal: 'DELETE' });
    this.onClose();
  };

  onClickPurge = () => {
    this.setState({ modal: 'PURGE' });
    this.onClose();
  };

  onResurrect = (): void => {
    const assets = this.getAssets();
    let { remotePaths } = filterAssets(assets, 0, true);
    if (remotePaths && remotePaths.length > 0) {
      new ResurrectQuery(this.getProgressOnQuery).run(assets, remotePaths);
    }
    this.onClose();
  };

  onPurgeClick = (): void => {
    const assets = this.getAssets();
    let { assetIds } = filterAssets(assets, -1, false, true);
    if (assetIds && assetIds.length > 0) {
      new PurgeQuery(this.getProgressOnQuery).run(assets, assetIds);
    }
    this.onClose();
  };

  runDelinkQuery = () => {
    new DelinkQueryExecutor(this.getAssets(), this.getProgressOnQuery).run();
    this.onClose();
  };

  /*
   *
   * Click Events Ends
   * */

  getExtraCheckoutUpdateOptions = () => {
    const items = [];
    items.push(
      <div key={0}>
        <Button onClick={() => this.onCheckoutClick(true, false)}>{t('WITH_LINKS')}</Button>
      </div>
    );
    items.push(
      <div key={2}>
        <Button onClick={() => this.onCheckoutClick(false, true)}>{t('WITH_LINK_PROXY')}</Button>
      </div>
    );
    items.push(
      <div key={3}>
        <Button onClick={() => this.onCheckoutClick(false, false)}>{t('ONLY_CURRENT')}</Button>
      </div>
    );

    return items;
  };

  getMoreItem = () => {
    const items = [];
    /*items.push(
      <div key={0}>
        <Button>{t('CREATE_JOB')}</Button>
      </div>
    );
    items.push(<Separator key={1}/>);
    items.push(
      <div key={2}>
        <Button>{t('SMART_LINK')}</Button>
      </div>
    );
    items.push(
      <div key={3}>
        <Button>{t('SMART_COPY')}</Button>
      </div>
    );
    items.push(<Separator key={4}/>);
    items.push(
      <div key={5}>
        <Button>{t('ARCHIVE_MENU')}</Button>
      </div>
    );
    */

    if (!this.containsNonAliveAsset && this.singleAsset) {
      items.push(this.showSpotLight(3));
      items.push(this.showAddToFavorite(3.1));
      if (!this.assetProps.containsFolder)
        items.push(MorePopover.addMenuItem(5.2, t('COPY_ASSET_URL'), this.copyAssetURL));
    }
    if (this.singleAsset && this.assetProps.containsFolder) {
      items.push(MorePopover.addMenuItem(5.7, t('FOLDER_DETAILS.FOLDER_DETAILS_OPTION'), this.folderDetailsView));
    }
    // if (this.singleAsset && !isAllFileSearchMode() && !isMobile()) {
    //   items.push(MorePopover.addMenuItem(30, t('BROWSE_TO_PARENT_FOLDER'), this.browserToParent()));
    // }
    items.push(MorePopover.addMenuItem(5, t('COPY_ASSET_ID'), this.copyAssetId));
    items.push(MorePopover.addMenuItem(5.1, t('COPY_ASSET_PATH'), this.copyAssetPath));
    items.push(<Separator key={31} />);
    items.push(
      <div key={6}>
        <Button onClick={this.onClickPurge}>{t('PURGE_MENU')}</Button>
      </div>
    );
    return items;
  };

  setUpBase = () => {
    let assets = this.getAssets();
    this.assetProps = getAssetProps(assets);
    this.singleAsset = assets.length === 1;
    this.containsNonAliveAsset =
      this.assetProps.containsDeleted || this.assetProps.containsArchived || this.assetProps.containsExpired;
  };

  isShowArchiveOption = () => {
    const assets: Array<RepoItem> = this.getAssets();
    if (assets.length) {
      if (assets.length === 1 && !FileInfoUtil.isFolder(assets[0])) {
        const lowResArchiveStatus = assets[0].getLowResArchiveStatus();
        return assets[0].isProxy() || lowResArchiveStatus == archiveStatusEnum.VAB_NOT_ARCHIVED ? true : false;
      } else {
        return true;
      }
    }
    return false;
  };

  isShowArchiveDetailsOption = () => {
    const assets: Array<RepoItem> = this.getAssets();
    if (assets.length === 1) {
      const asset: RepoItem = assets[0];
      if (
        asset.getLowResArchiveStatus() === archiveStatusEnum.VAB_COPIED_TO_ARCHIVE ||
        asset.getMidResArchiveStatus() === archiveStatusEnum.VAB_COPIED_TO_ARCHIVE ||
        asset.getHiResArchiveStatus() === archiveStatusEnum.VAB_COPIED_TO_ARCHIVE ||
        asset.getLowResArchiveStatus() === archiveStatusEnum.VAB_ARCHIVED ||
        asset.getMidResArchiveStatus() === archiveStatusEnum.VAB_ARCHIVED ||
        asset.getHiResArchiveStatus() === archiveStatusEnum.VAB_ARCHIVED
      ) {
        return true;
      } else return false;
    } else return false;
  };

  isShowArchiveRestoreOption = () => {
    const assets: Array<RepoItem> = this.getAssets();

    if (assets.length) {
      if (assets.length === 1 && !FileInfoUtil.isFolder(assets[0])) {
        const lowResArchiveStatus: string = assets[0].getLowResArchiveStatus();
        const midResArchiveStatus: string = assets[0].getMidResArchiveStatus();
        const hiResArchiveStatus: string = assets[0].getHiResArchiveStatus();
        const archived: boolean = assets[0].isArchived();
        if (!assets[0].isProxy()) {
          return archived ? true : false;
        } else {
          return (lowResArchiveStatus !== archiveStatusEnum.VAB_NOT_ARCHIVED &&
            lowResArchiveStatus !== archiveStatusEnum.VAB_NOT_APPLICABLE) ||
            (midResArchiveStatus !== archiveStatusEnum.VAB_NOT_ARCHIVED &&
              midResArchiveStatus !== archiveStatusEnum.VAB_NOT_APPLICABLE) ||
            (hiResArchiveStatus !== archiveStatusEnum.VAB_NOT_ARCHIVED &&
              hiResArchiveStatus !== archiveStatusEnum.VAB_NOT_APPLICABLE)
            ? true
            : false;
        }
      } else {
        return true;
      }
    }
    return false;
  };

  getItem = () => {
    this.contextMenuCall = this.props.showPopover;
    this.setUpBase();
    const items = [];
    const isNotProject = !FileInfoUtil.isProject(this.props.asset);
    const isAssetTab = ViewUtil.isAssetTabActive();
    const isBrowseExternalTab = ViewUtil.isBrowseExternalTabActive();
   
    if (System.isVAB() && isAssetTab) {
      if (this.singleAsset) {
        this.containToRefs = this.props.asset.isHasToLinks();
      }

      if (this.assetProps.allFromWC) {
        const statusObject = this.props.asset.wcStatusObject;
        const modTime = statusObject && statusObject.getModTime() ? statusObject.getModTime() : '';
        const contextMenuStatusCheckIn = {
          menuStatus: t('CHECK_IN'),
          modStat: this.singleAsset
            ? t('MODIFIED_ON', { TIME: modTime })
            : this.assetProps.containsModified
            ? t('CHECK_IN') + ' ' + t(MODIFIED) + ' ' + t('FILES')
            : ' '
        };
        const contextMenuStatusUpdate =
          this.assetProps.containsConflict || this.assetProps.containsPotentialConflict
            ? {
                menuStatus: t('CONFLICT_MENU'),
                modStat: this.assetProps.containsPotentialConflict
                  ? t('ACTION_DETECTED', {
                      ACTION: 'Potential Conflict',
                      TIME: modTime
                    })
                  : t('ACTION_DETECTED', { ACTION: 'Conflict', TIME: modTime })
              }
            : {
                menuStatus: this.assetProps.containsUpdatePending ? t('UPDATE') : t('UP_TO_DATE'),
                modStat: !this.assetProps.containsUpdatePending
                  ? t('LAST_UPDATED', { TIME: modTime })
                  : !this.singleAsset
                  ? t('UPDATE') + ' ' + t('FILES')
                  : undefined
              };
        // items.push(
        if (this.assetProps.containsConflict) {
          items.push(this.getConflictMenu(contextMenuStatusUpdate));
        } else if (
          this.assetProps.containsUpdatePending ||
          this.assetProps.containsPotentialConflict ||
          !this.assetProps.containsModified ||
          this.containsNonAliveAsset
        ) {
          this.containToRefs && this.assetProps.containsUpdatePending && false
            ? items.push(
                <SubMenuOptions
                  items={this.getExtraCheckoutUpdateOptions()}
                  key={1}
                  isComponent={true}
                  Component={this.getUpdateMenu(contextMenuStatusUpdate)}
                />
              )
            : items.push(this.getUpdateMenu(contextMenuStatusUpdate));
        } else {
          items.push(
            <Button key={0} className="statusItem" onClick={this.onCheckInClick}>
              <AssetStatusView
                isNextLine={true}
                asset={this.props.asset}
                isContextMenu={true}
                contextMenuStatus={contextMenuStatusCheckIn}
                status={
                  this.singleAsset ? statusObject._wcStatus : this.assetProps.containsModified ? MODIFIED : VERSIONED
                }
              />
            </Button>
          );
        }
        // );
      } else if (!this.assetProps.containWCFile && !this.containsNonAliveAsset) {
        this.containToRefs
          ? items.push(
              <SubMenuOptions
                items={this.getExtraCheckoutUpdateOptions()}
                key={1}
                isComponent={true}
                Component={this.getCheckoutMenu()}
              />
            )
          : items.push(this.getCheckoutMenu());
      }
      if (items.length > 0) {
        items.push(<Separator key={2} />);
      }
    }

    if (!this.containsNonAliveAsset && this.singleAsset && !isBrowseExternalTab) {
      if (!this.assetProps.containsFolder && System.isVAB()) {
        if (this.assetProps.containWCFile) items.push(MorePopover.addMenuItem(34, t('EDIT_MENU'), this.onEditClick));
        //EDIT
        else items.push(MorePopover.addMenuItem(34, t('OPEN_MENU'), this.onOpenClick)); //OPEN
      }
      items.push(this.showRename(4));
      if (ViewUtil.isCollectionTabActive()) {
        items.push(this.showRemoveFromCollection(12));
      }
    }
    if (isAssetTab) {
      if (!this.containsNonAliveAsset) {
        if (
          !this.containsNonAliveAsset &&
          (this.assetProps.lockMenuStatus === 1 || this.assetProps.lockMenuStatus === 2) &&
          isNotProject
        ) {
          this.addSeparator(items, 19);
          if (this.assetProps.containsFolder) {
            // for lock
            items.push(this.addLockUnLockStatus(20, 2));
          }

          if (!this.assetProps.containsFolder) {
            // for lock
            items.push(this.addLockUnLockStatus(22, this.assetProps.lockMenuStatus));
          }
          items.push(<Separator key={21} />);
        }

        if (!ViewUtil.isCollectionTabActive() && isNotProject && !this.assetProps.containsFolder) {
          // items.push(<Separator key={22} />);
          items.push(MorePopover.addMenuItem(7, t('ADD_TO_COLLECTION'), this.addToCollection));
        }
        items.push(MorePopover.addMenuItem(8, t('SHARE_AND_DOWNLOAD'), this.onDownloadClick));

        if (!this.assetProps.containsFolder) {
          items.push(MorePopover.addMenuItem(10, t('EDIT_METADATA'), this.editMetadata));
        }

        items.push(MorePopover.addMenuItem(39, t('SMART_COPY'), () => this.onSmartCopyLink(SMART_COPY_MODAL)));
        items.push(MorePopover.addMenuItem(40, t('SMART_LINK'), () => this.onSmartCopyLink(SMART_LINK_MODAL)));

        if (isNotProject) {
          items.push(<Separator key={9} />);
          items.push(
            <div key={13} onClick={this.onClickDelete}>
              {t('DELETE_LABEL')}
            </div>
          );
          if (isShowCompareViewMenu(this.getAssets())) {
            items.push(
              <div key={29} onClick={this.compareView}>
                {t('COMPARE_BOTH')}
              </div>
            );
          }
        }
      }

      if (System.isDesktop()) {
        if (this.isShowArchiveRestoreOption()) {
          items.push(
            MorePopover.addMenuItem(27, t('RESTORE'), () => {
              this.setState({ modal: RESTORE_MODAL });
              this.onClose();
            })
          );
        }
        if (this.isShowArchiveOption()) {
          items.push(
            MorePopover.addMenuItem(29, t('ARCHIVE_TEXT'), () => {
              this.setState({ modal: ARCHIVE_MODAL });
              this.onClose();
            })
          );
        }
        if (this.isShowArchiveDetailsOption()) {
          items.push(
            MorePopover.addMenuItem(29.1, t('ARCHIVE_DETAILS'), () => {
              this.setState({ modal: ARCHIVE_DETAILS_MODAL });
              this.onClose();
            })
          );
        }
      }

      if (this.assetProps.allFromWC) {
        items.push(MorePopover.addMenuItem(14, t('REMOVE_WORKING_COPY_MENU'), this.runDelinkQuery));
      }

      if (this.assetProps.containsDeleted && !this.assetProps.containsAlive) {
        this.addSeparator(items, 24);
        items.push(MorePopover.addMenuItem(25, t('RESURRECT'), this.onResurrect));
      }

      const moreItems = this.getMoreItem();
      if (moreItems && moreItems.length > 0 && isNotProject) {
        if (items.length > 0) items.push(<Separator key={16} />);
        if (isSmallScreen()) {
          items.push(moreItems);
        } else {
          items.push(<SubMenuOptions menuName={t('MORE')} items={moreItems} key={11} />);
        }
      }
    } else if(!isBrowseExternalTab) {
      items.push(MorePopover.addMenuItem(5, t('COPY_ASSET_ID'), this.copyAssetId));
      items.push(MorePopover.addMenuItem(5.1, t('COPY_ASSET_PATH'), this.copyAssetPath));
    }
    if (this.singleAsset && System.isDesktop()) {
      items.push(<Separator key={17} />);
      items.push(MorePopover.addMenuItem(26, 'Show in Asset Browser', this.showOnLegacyBrowser));
      if (this.assetProps.allFromWC) items.push(this.showReveal(15));
    }

    
    if (items.length === 1 && items[0].props.items) {
      return items[0].props.items;
    }

    if(isBrowseExternalTab) {
      const {extDetails} = this.props;
      items.push(MorePopover.addMenuItem(32, t('INGEST_TO_ZOOM'), ()=>{
        this.setState({ modal: S3_UPLOAD_MODAL, showPopover: false });
        this.onClose();
      }));
      items.push(MorePopover.addMenuItem(33, t('REFRESH'), ()=>{
        this.props.onRefresh(extDetails);
        this.onClose();
      }));
     
      if(BrowseExtUtil.isFile(extDetails.node.getType())) {
        items.push(<Separator key={34} />);
        items.push(MorePopover.addMenuItem(35, t('DETAILS'), ()=> {
          this.setState({modal: EXT_FILE_INFO_MODAL, showPopover: false});
          this.onClose();
        }));
      }
    }

    return items;
  };

  compareView = () => {
    this.setState({ modal: COMPARE_VIEW_MODAL });
    this.onClose();
  };

  folderDetailsView = () => {
    this.setState({ modal: FOLDER_DETAILS });
    this.onClose();
  };

  addSeparator(items, key) {
    if (items.length > 0 && items[items.length - 1].type.name !== 'Separator') items.push(<Separator key={key} />);
  }

  static addMenuItem(__key, __label, onClickFunction) {
    return (
      <div key={__key} onClick={onClickFunction}>
        <Button>{__label}</Button>
      </div>
    );
  }

  /*static addMultiLineMenu(__key, menu, label, funcLink) {
      return <div key={__key}>
        <Button onClick={funcLink}>
          <div>{menu}</div>
          <div className="menu-info">{label}</div>
        </Button>
      </div>;
    }*/

  getConflictMenu(contextMenuStatusObject) {
    return (
      <Button key={1} className="statusItem" onClick={this.onConflictResolveClick}>
        <AssetStatusView
          isNextLine={true}
          asset={this.props.asset}
          isContextMenu={true}
          contextMenuStatus={contextMenuStatusObject}
          status={CONFLICT}
        />
      </Button>
    );
  }

  addLockUnLockStatus(__Key, __stat) {
    if (__stat === 1) return MorePopover.addMenuItem(23, t('LOCK'), this.onLockClick);
    return MorePopover.addMenuItem(23, t('UNLOCK'), this.onUnLockClick);
  }

  getUpdateMenu(contextMenuStatusUpdate) {
    return (
      <Button key={1} className="statusItem" onClick={this.onUpdateClick}>
        <AssetStatusView
          isNextLine={true}
          asset={this.props.asset}
          isContextMenu={true}
          contextMenuStatus={contextMenuStatusUpdate}
          status={
            this.assetProps.containsUpdatePending || this.assetProps.containsPotentialConflict
              ? UPTODATE_NEEDED_ICON
              : VERSIONED
          }
        />
      </Button>
    );
  }

  getCheckoutMenu = () => {
    return (
      <Button key={1} className="statusItem" onClick={() => this.onCheckoutClick(false, false)}>
        <span>
          <img src={ThemeSetup.getImageURL('checkout.svg')} style={{ marginBottom: '3px', padding: 'unset' }} />
        </span>
        <span style={{ paddingRight: '5px', marginLeft: '5px' }}>{t('CHECKOUT')}</span>
        <div className="mtimeWithStatus">
          <AssetStatusView isNextLine={true} asset={this.props.asset} isContextMenu={true} />
        </div>
      </Button>
    );
  };

  onRef = (element: ?HTMLDivElement) => {
    if (!element) {
      return;
    }
    this.portalElement = element.parentNode;
    if (this.portalElement) {
      this.portalElement.onmousedown = (e) => {
        if (e.which === 3) {
          this.onClose();
        }
      };
    }
    if (this.props.showPopover) {
      getPosition(element, this.props.showPopover.screenX, this.props.showPopover.screenY);
    } else {
      const domElement = this.refs.icon.getBoundingClientRect();
      getPosition(element, domElement.x, domElement.y);
    }
  };

  onClose = () => {
    this.props.closeContextMenu();
    this.setState({ showPopover: false });
  };

  setModalToOnClose = (isEventOrBool) => {
    this.setState({ modal: null });
    if(typeof(isEventOrBool) === 'boolean') {
      this.props.onRefreshPWD ? this.props.onRefreshPWD() : null;
    }
  }

  render() {
    const { modal } = this.state;
    const { top, left, right, bottom, width, height, backgroundImage, asset } = this.props;
    //const items = this.getItem();
    this.selectedAssetsCache = undefined;
    return (
      <div name="MorePopover" onClick={(event) => event.stopPropagation()}>
        <div
          ref="icon"
          className="icon"
          onClick={(event) => {
            this.setState({ showPopover: !this.state.showPopover }, () => {
              if (!this.state.showPopover) {
                this.props.closeContextMenu();
              } else {
                if (this.props.isPopOverOpen) {
                  this.props.isPopOverOpen(true);
                }
              }
            });
            event.stopPropagation();
            return false;
          }}
          style={{
            ...styles.moreIcon,
            top,
            left,
            right,
            bottom,
            width,
            height,
            backgroundImage,
            ...this.props.iconStyle
          }}
        />
        {this.state.showPopover || this.props.showPopover ? (
          <Portal defaultOpen={true} closeOnEsc={true} closeOnOutsideClick={true} onClose={this.onClose}>
            <Popover getRef={this.onRef} onClose={this.onClose}>
              <div name="MorePopover_Portal">{this.getItem().map((value) => value)}</div>
            </Popover>
          </Portal>
        ) : null}
        {modal === 'RENAME' ? (
          <RenameModal
            asset={this.props.asset}
            onClose={() => this.setStateOnMountCheck({ modal: null })}
            fetchAssets={this.props.onRenameSuccess} //fetchAssets props call onsuccess of rename so pass rename success in Same props.
          />
        ) : null}
        {modal === S3_UPLOAD_MODAL ? (
        <S3Upload
        title={t('UPLOAD_FROM_URL')} 
        onClose={this.setModalToOnClose}
        extDetails={this.props.extDetails}
        />) : null}
        {modal === EXT_FILE_INFO_MODAL ? (
          <InfoModal
          extDetails={this.props.extDetails}
          onClose={this.setModalToOnClose}
          ></InfoModal>
        ): null}
        {modal === 'SPOTLIGHT_ADD' ? (
          <AddSpotlightModal asset={this.props.asset} onClose={this.setModalToOnClose} />
        ) : null}
        {modal === 'DELETE' ? (
          <DeleteAssetsModal
            assets={this.getAssets()}
            onDeleteSuccess={this.props.onDeleteSuccess}
            onClose={this.setModalToOnClose}
            actionLabel={t('DELETE_LABEL')}
          />
        ) : null}
        {modal === 'PURGE' ? (
          <DeleteAssetsModal
            assets={this.getAssets()}
            onDeleteSuccess={this.onPurgeClick}
            message={t('CONFIRM_PURGE')}
            onClose={this.setModalToOnClose}
            actionLabel={t('PURGE_MENU')}
          />
        ) : null}
        {modal === 'REMOVE' ? (
          <RemoveAssetsModal
            assets={this.getAssets()}
            onDeleteSuccess={this.props.onDeleteSuccess}
            onClose={this.setModalToOnClose}
          />
        ) : null}
        {modal === ADD_TO_COLLECTION ? (
          <AddToCollectionModal asset={this.getAssets()} onClose={this.setModalToOnClose} />
        ) : null}
        {modal === EDIT_METADATA_MODAL ? (
          <EditAssetsModal assets={this.getAssets()} onClose={this.setModalToOnClose} />
        ) : null}
        {modal === PROGRESS_MODAL ? (
          <ProgressDialog
            isRunningBackground={false}
            loaded={this.state.progress || 0}
            isCancelAble={false}
            onClose={this.setModalToOnClose}
            message={this.state.message || 0}
          />
        ) : null}
        {modal === RESTORE_MODAL ? (
          <RestoreModal
            assets={this.getAssets()}
            onRefreshPWD={this.props.onRefreshPWD}
            onClose={this.setModalToOnClose}
          />
        ) : null}
        {modal === ARCHIVE_MODAL ? (
          <ArchiveModal
            assets={this.getAssets()}
            onRefreshPWD={this.props.onRefreshPWD}
            onClose={this.setModalToOnClose}
          />
        ) : null}
        {modal === ARCHIVE_DETAILS_MODAL ? (
          <ArchiveDetailsModal assets={this.getAssets()} onClose={this.setModalToOnClose} />
        ) : null}

        {modal === SMART_COPY_MODAL || modal === SMART_LINK_MODAL ? (
          <SmartCopyLinkDialog
            assets={this.getAssets()}
            isSmartCopy={modal === SMART_COPY_MODAL}
            onClose={this.setModalToOnClose}
          />
        ) : null}
        {modal === COMPARE_VIEW_MODAL ? (
          <CompareView assets={this.getAssets()} onClose={this.setModalToOnClose} />
        ) : null}
        {modal === FOLDER_DETAILS ? (
          <FolderDetails assets={this.getAssets()} onClose={this.setModalToOnClose} />
        ) : null}
      </div>
    );
  }

  renderAssetsDetailAction() {
    return (
      <Action custom predicates={[() => ViewUtil.isCollectionTabActive()]}>
        <div onClick={this.onAssetDetailsClick}>{t('VIEW_ASSET_DETAILS')}</div>
      </Action>
    );
  }

  onAssetDetailsClick = (e) => {
    this.setState({
      modal: 'ASSET_DETAIL'
    });
    this.onClose();
    this.props.closeContextMenu();
    this.switchToAssetDetails();
  };

  showReveal(key) {
    return (
      <Action key={key} custom predicates={[() => this.canShowReveal()]}>
        <div onClick={(e) => this.runRevealExecQuery()}>
          {this.isMac ? t('REVEAL_IN_FINDER') : t('REVEAL_IN_EXPLORER')}
        </div>
      </Action>
    );
  }

  canShowReveal() {
    return this.singleAsset && this.assetProps.containWCFile;
  }

  switchToAssetDetails() {
    if (this.props.showAssetDetail) {
      this.props.showAssetDetail();
    }
  }

  showSpotLight(key) {
    return (
      <Action
        key={key}
        custom
        predicates={[() => !ViewUtil.isCollectionTabActive() || this.props.permission.canShowSpotLightAction]}>
        <div
          onClick={(e) => this.setState({ modal: 'SPOTLIGHT_ADD', showPopover: false }, this.props.closeContextMenu)}>
          {t('ADD_TO_SPOTLIGHT')}
        </div>
      </Action>
    );
  }

  showAddToFavorite(key) {
    const { closeContextMenu, asset, toggelIsFav } = this.props;
    const isFav = FileInfoUtil.isFavAsset(asset.getFuid());
    return (
      <Action
        key={key}
        custom
        predicates={[() => !ViewUtil.isCollectionTabActive() || this.props.permission.canShowSpotLightAction]}>
        <div
          onClick={(e) => {
            isFav ? removeFavItem(asset) : favAddRemoveOperation(asset);
            closeContextMenu();
            if (toggelIsFav) toggelIsFav();
          }}>
          {isFav ? t('REMOVE_FROM_FAVORITE') : t('MARK_AS_FAVORITE')}
        </div>
      </Action>
    );
  }

  showRename(key) {
    return (
      <Action
        key={key}
        custom
        predicates={[
          () =>
            (!ViewUtil.isCollectionTabActive() || this.props.permission.canRenameCollection) &&
            !FileInfoUtil.isProject(this.props.asset)
        ]}>
        <div onClick={(e) => this.setState({ modal: 'RENAME', showPopover: false }, this.props.closeContextMenu)}>
          {t('RENAME_LABEL')}
        </div>
      </Action>
    );
  }

  showRemoveFromCollection(key) {
    return (
      <Action
        key={key}
        custom
        predicates={[() => !ViewUtil.isCollectionTabActive() || this.props.permission.canRemoveAssets]}>
        <div onClick={(e) => this.setState({ modal: 'REMOVE', showPopover: false }, this.props.closeContextMenu)}>
          {t('REMOVE_FROM_COLLECTION')}
        </div>
      </Action>
    );
  }

  deleteAsset() {
    this.props.deleteAsset();
  }

  onCheckoutClick(withLinks, withLinkAndProxy) {
    let checkoutController = new CheckoutController(
      this.getAssets(),
      withLinks,
      withLinkAndProxy,
      this.getProgressOnQuery
    );
    checkoutController.run();
    this.onClose();
  }

  queryStatus: string;
  getProgressOnQuery = (progress, message, status = '', showMsg = true) => {
    if (progress < 0) {
      progress = 0;
    }
    if (this.queryStatus !== status) {
      this.queryStatus = status;
      status === '' || status === null
        ? this.setStateOnMountCheck({ modal: null })
        : status === 'RUNNING'
        ? this.setStateOnMountCheck({ modal: PROGRESS_MODAL })
        : '';
    }
    if (status === 'RUNNING') this.setStateOnMountCheck({ progress, message });
    else {
      this.setStateOnMountCheck({ modal: null });
      if (showMsg) {
        showSuccessMsg(message ? message : t('COMPLETED_SUCCESSFULLY'));
      }
    }
  };
}

MorePopover.reasonableMargin = 300;
MorePopover.reasonableHeightMarcgin = 100;

const styles = {
  moreIcon: {
    position: 'absolute',
    borderRadius: '50%',
    boxShadow: '0 1px 4px 0 rgba(0,0,0,0.04)',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '80%',
    cursor: 'pointer'
  }
};

type SubMenuOptionProps = {
  items: array<any>,
  key: number,
  menuName: string,
  icon: ?string,
  iconOnHover: ?string,
  isComponent: ?boolean,
  Component: ?any
};

export const SubMenuOptions = function(props: SubMenuOptionProps) {
  const [showOptions: boolean, setShowOptions: (s: boolean) => void] = useState(false);
  const [info: any, setInfo: (in) => void] = useState(null);
  const onclick = useCallback((e: MouseEvent) => {
    const infos = e.currentTarget;
    setShowOptions(!showOptions);
    setTimeout(() => {
      getMorePos(infos);
    }, 200);
  });
  const toHide = useCallback(() => {
    setShowOptions(false);
  });
  const icon = showOptions ? (props.iconOnHover ? props.iconOnHover : props.icon || null) : props.icon || null;
  return (
    <React.Fragment>
      <div className={showOptions ? 'selected' : ''} onMouseEnter={(event) => onclick(event)} onMouseLeave={toHide}>
        <div className={'flex-row align-center'}>
          {props.isComponent ? (
            props.Component
          ) : (
            <Button icon={icon} type="PLAIN" text={props.menuName} onClick={noop} />
          )}
          <Button
            className={classNames('ExpandButton', { selected: showOptions })}
            icon={showOptions ? 'images/triangle_gray_selected.svg' : 'images/triangle_gray.svg'}
            onClick={noop}
          />
        </div>
        {showOptions ? (
          <div name="MorePopover_Portal" className="moreOption Submenu">
            {props.items.map((value) => value)}
          </div>
        ) : null}
      </div>
    </React.Fragment>
  );
};

function getMorePos(ref: HTMLElement) {
  const element = ref.offsetParent.querySelector('.moreOption');
  if (!element) return;
  const offsetWidth = element.offsetParent.offsetWidth;
  element.style.width = `${offsetWidth}px`;
  const clickX = element.offsetParent.offsetWidth + element.offsetParent.offsetLeft;
  const clickY = element.offsetParent.offsetHeight + element.offsetParent.offsetTop;
  const screenW = window.innerWidth;
  const screenH = window.innerHeight;
  const rootW = element.offsetWidth;
  const rootH = element.offsetHeight;
  const right = screenW > clickX + rootW;
  const left = !right;
  const top = screenH > clickY + rootH;
  const bottom = !top;
  if (right) element.style.left = `100%`;
  if (left) element.style.left = `-100%`;
  if (top) element.style.top = `${ref.offsetTop - 6}px`;
  if (bottom) element.style.top = `${ref.offsetTop - element.offsetHeight + ref.offsetHeight + 6}px`;
  element.style.opacity = 'unset';
}

function getPosition(rect, clientX, clientY) {
  const clickX = clientX;
  const clickY = clientY;
  const screenW = window.innerWidth;
  const screenH = window.innerHeight;
  const rootW = rect.offsetWidth;
  const rootH = rect.offsetHeight;
  const right = screenW - clickX > rootW;
  const left = !right;
  const top = screenH - clickY > rootH;
  let moveTop = screenH - clientY;
  // if (clickY < rootH) {
  //   moveTop = rootH - clickY;
  // }
  const bottom = !top;
  if (right) rect.style.left = `${clickX + 5}px`;
  if (left) rect.style.left = `${clickX - rootW - 5}px`;
  if (top) rect.style.top = `${clickY + 5}px`;
  if (bottom) rect.style.top = `${clickY - rootH + moveTop}px`;
}

const isShowCompareViewMenu = (assets: RepoItem[]) => {
  if (assets.length !== 2) return false;
  if (FileInfoUtil.isImage(assets[0]) && FileInfoUtil.isImage(assets[1])) return true;
  if (FileInfoUtil.isVideo(assets[0]) && FileInfoUtil.isVideo(assets[1])) return true;
  if (FileInfoUtil.isMultiPage(assets[0]) && FileInfoUtil.isMultiPage(assets[1])) return true;
  return false;
};
